import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db, supabase } from '../lib/supabase';

const getCharValue = (char) => {
  const c = char.toUpperCase();
  if (c >= '0' && c <= '9') {
    return parseInt(c);
  }
  if (c >= 'A' && c <= 'Z') {
    const pos = c.charCodeAt(0) - 64;
    return ((pos - 1) % 9) + 1;
  }
  return 0;
};

const getNumerologySum = (str) => {
  let sum = 0;
  for (const char of str) {
    sum += getCharValue(char);
  }
  return sum;
};

const reduceToSingleDigit = (num) => {
  while (num > 9) {
    num = num.toString().split('').reduce((acc, d) => acc + parseInt(d), 0);
  }
  return num;
};

const getNumerologyValue = (str) => {
  return reduceToSingleDigit(getNumerologySum(str));
};

const findSuffixFor8 = (baseCode) => {
  const currentSum = getNumerologySum(baseCode);

  for (let i = 1; i <= 9; i++) {
    if (reduceToSingleDigit(currentSum + i) === 8) {
      return i.toString();
    }
  }

  for (let i = 10; i <= 99; i++) {
    const digitSum = i.toString().split('').reduce((acc, d) => acc + parseInt(d), 0);
    if (reduceToSingleDigit(currentSum + digitSum) === 8) {
      return i.toString();
    }
  }

  return '8';
};

const generateRandomString = (length) => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const generateRandomNumerology8Code = () => {
  const randomPart = generateRandomString(5);
  const baseCode = `path-${randomPart}`;
  const currentValue = getNumerologyValue(baseCode);

  if (currentValue === 8) {
    return baseCode;
  }

  const suffix = findSuffixFor8(baseCode);
  return `${baseCode}${suffix}`;
};

const censorEmail = (email) => {
  if (!email || !email.includes('@')) return '***';
  const [local, domain] = email.split('@');
  const dotIdx = domain.lastIndexOf('.');
  const domainName = domain.substring(0, dotIdx);
  const tld = domain.substring(dotIdx);
  const localCensored = local.length <= 2 ? local[0] + '***' : local.slice(0, 2) + '***' + local.slice(-1);
  const domainCensored = domainName[0] + '***';
  return `${localCensored}@${domainCensored}${tld}`;
};

const EarnPage = () => {
  const { user, profile, updateProfile } = useAuth();
  const [referralCode, setReferralCode] = useState('');
  const [newReferralCode, setNewReferralCode] = useState('');
  const [isSettingCode, setIsSettingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [codeSuccess, setCodeSuccess] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [referralStats, setReferralStats] = useState({
    totalUsers: 0,
    totalBonus: 0,
    approvedBonus: 0,
  });
  const [referredUsers, setReferredUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.referral_code) {
      setReferralCode(profile.referral_code);
    }
    if (user?.id) {
      fetchReferralData();
    } else {
      setLoading(false);
    }
  }, [profile, user]);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_my_referrals');

      if (!error && data) {
        const merged = data.map((r) => ({
          id: r.id,
          email: r.email,
          created_at: r.created_at,
          is_paid: r.is_paid || false,
          bonus_amount: parseFloat(r.bonus_amount || 0),
        }));

        setReferredUsers(merged);

        const totalBonus = merged.reduce((sum, r) => sum + r.bonus_amount, 0);
        const approvedBonus = merged
          .filter((r) => r.is_paid)
          .reduce((sum, r) => sum + r.bonus_amount, 0);

        setReferralStats({
          totalUsers: merged.length,
          totalBonus,
          approvedBonus,
        });
      }
    } catch (err) {
    }
    setLoading(false);
  };

  const generateNumerology8Code = (userInput) => {
    const baseCode = `path-${userInput.toLowerCase()}`;
    const currentValue = getNumerologyValue(baseCode);

    if (currentValue === 8) {
      return baseCode;
    }

    const suffix = findSuffixFor8(baseCode);
    return `${baseCode}${suffix}`;
  };

  useEffect(() => {
    if (newReferralCode.trim() && /^[a-zA-Z0-9]+$/.test(newReferralCode)) {
      const code = generateNumerology8Code(newReferralCode);
      setGeneratedCode(code);
    } else {
      setGeneratedCode('');
    }
  }, [newReferralCode]);

  const handleSetReferralCode = async () => {
    if (profile?.referral_code) {
      setCodeError('You already have a referral code. Each user can only have one permanent code.');
      return;
    }

    setIsSettingCode(true);
    setCodeError('');
    setCodeSuccess('');

    try {
      let finalCode = generateRandomNumerology8Code();
      let attempts = 0;
      const maxAttempts = 20;

      while (attempts < maxAttempts) {
        const { exists } = await db.referrals.checkCodeExists(finalCode);

        if (!exists) {
          break;
        }

        finalCode = generateRandomNumerology8Code();
        attempts++;
      }

      if (attempts >= maxAttempts) {
        setCodeError('Unable to generate unique code. Please try again.');
        setIsSettingCode(false);
        return;
      }

      const result = await updateProfile({ referral_code: finalCode });

      if (result.success) {
        setReferralCode(finalCode);
        setNewReferralCode('');
        setGeneratedCode('');
        setCodeSuccess('Referral code generated successfully!');
      } else {
        setCodeError(result.error || 'Failed to generate referral code');
      }
    } catch (err) {
      setCodeError('Failed to generate referral code');
    }

    setIsSettingCode(false);
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/signup?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="earn-page">
      <div className="earn-header">
        <h1 className="page-title">Earn With Us</h1>
        <p className="earn-subtitle">Refer friends and earn bonus bandwidth — rewards are verified and approved by our team.</p>
      </div>

      <div className="earn-section referral-code-section">
        <div className="section-header-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="8.5" cy="7" r="4"/>
            <line x1="20" y1="8" x2="20" y2="14"/>
            <line x1="23" y1="11" x2="17" y2="11"/>
          </svg>
        </div>
        <h2>Your Referral Code</h2>
        <p className="section-description">Create your unique referral code to start earning bandwidth rewards.</p>

        {referralCode ? (
          <div className="referral-code-display">
            <div className="code-box">
              <span className="code-label">YOUR CODE <span className="permanent-badge">PERMANENT</span></span>
              <span className="code-value">{referralCode.toUpperCase()}</span>
            </div>
            <div className="referral-link-box">
              <span className="link-label">REFERRAL LINK</span>
              <div className="link-row">
                <input
                  type="text"
                  value={`${window.location.origin}/signup?ref=${referralCode}`}
                  readOnly
                  className="link-input"
                />
                <button className="copy-btn" onClick={copyReferralLink}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  COPY
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="no-referral-code">
            <p>No referral code assigned yet. Contact support to get your referral code.</p>
          </div>
        )}
      </div>

      <div className="earn-stats-grid">
        <div className="earn-stat-card">
          <div className="stat-icon users">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">REFERRED USERS</span>
            <span className="stat-value">{loading ? '...' : referralStats.totalUsers}</span>
            <span className="stat-sublabel">People using your code</span>
          </div>
        </div>

        <div className="earn-stat-card highlight">
          <div className="stat-icon earned">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">YOUR EARNINGS</span>
            <span className="stat-value earned-value">{loading ? '...' : referralStats.totalBonus % 1 === 0 ? referralStats.totalBonus.toFixed(0) : referralStats.totalBonus.toFixed(1)} <span className="unit">GB</span></span>
            <span className="stat-sublabel">{referralStats.approvedBonus > 0 ? `${referralStats.approvedBonus % 1 === 0 ? referralStats.approvedBonus.toFixed(0) : referralStats.approvedBonus.toFixed(1)} GB approved` : 'Pending approval'}</span>
          </div>
        </div>
      </div>

      <div className="earn-section earnings-info">
        <div className="section-header-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        </div>
        <h2>How It Works</h2>
        <div className="earnings-breakdown">
          <div className="earnings-step">
            <span className="step-number">01</span>
            <div className="step-content">
              <h4>Share Your Code</h4>
              <p>Share your unique referral code with friends and colleagues</p>
            </div>
          </div>
          <div className="earnings-step">
            <span className="step-number">02</span>
            <div className="step-content">
              <h4>They Sign Up</h4>
              <p>When they create an account using your code, they're linked to your referral</p>
            </div>
          </div>
          <div className="earnings-step">
            <span className="step-number">03</span>
            <div className="step-content">
              <h4>Earn Bonus Bandwidth</h4>
              <p>Our team reviews referrals and approves bonus bandwidth rewards for both you and your referrals</p>
            </div>
          </div>
        </div>
      </div>

      <div className="earn-section referred-users-section">
        <div className="section-header">
          <div className="section-header-left">
            <div className="section-header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <h2>Referred Users</h2>
          </div>
          <span className="users-count">{referralStats.totalUsers} USERS</span>
        </div>

        <div className="referred-users-table">
          <div className="table-header">
            <span className="col-id">ID</span>
            <span className="col-user">USER</span>
            <span className="col-status">STATUS</span>
            <span className="col-earned">BONUS</span>
            <span className="col-date">DATE JOINED</span>
          </div>

          <div className="table-body">
            {loading ? (
              <div className="table-empty">
                <p>LOADING...</p>
              </div>
            ) : referredUsers.length === 0 ? (
              <div className="table-empty">
                <div className="empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <line x1="17" y1="11" x2="23" y2="11"/>
                  </svg>
                </div>
                <p>NO REFERRED USERS YET</p>
                <span>Share your referral code to start earning</span>
              </div>
            ) : (
              referredUsers.map((refUser, index) => (
                <div key={refUser.id} className="table-row">
                  <span className="col-id">{index + 1}</span>
                  <span className="col-user">
                    <span className="user-email">{refUser.email ? censorEmail(refUser.email) : `User #${refUser.id?.slice(0, 8)}`}</span>
                  </span>
                  <span className="col-status">
                    <span className={`status-badge ${refUser.is_paid ? 'approved' : 'pending'}`}>
                      {refUser.is_paid ? 'APPROVED' : 'PENDING REVIEW'}
                    </span>
                  </span>
                  <span className="col-earned highlight">{refUser.bonus_amount > 0 ? `${refUser.bonus_amount % 1 === 0 ? refUser.bonus_amount.toFixed(0) : refUser.bonus_amount.toFixed(1)} GB` : '—'}</span>
                  <span className="col-date">{formatDate(refUser.created_at)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarnPage;
